import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import relationships from 'facet-rest/src/consts/relationships';
import { Tooltip as MuiTooltip, useTheme, withStyles } from '@material-ui/core';
import { CheckCircle, Description } from '@material-ui/icons';
import { openModal } from 'Helpers/ModalHelper';
import { buildModalQuery } from 'Consts/ModalQueries';
import modalConsts from 'Consts/ModalNames';
import Uploader from 'Components/Files/GenericUploader/Uploader';
import fr from 'facet-rest';
import LinearProgress from 'Components/UI/LinearProgress';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import EditIcon from 'Icons/Edit';
import { addQuery } from 'Helpers/Query';
import useUserContext from 'Context/UserContext';
import { navigateToClientPage } from 'Views/Page/Content/Templates';
import { navigateToFunding } from 'Helpers/AccountFunding';
import RoundButton from '../../RoundButton';
import AccountPicklist from '../AccountPicklist';

const styles = theme => ({
  root: {},
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  progressBar: {
    maxHeight: 24,
    margin: theme.spacing(0, 1, 1, 1),
    minWidth: 100,
    '& .MuiLinearProgress-root': {
      borderRadius: theme.radius.lg,
    },
  },
  [theme.breakpoints.down('md')]: {
    root: {
      justifyContent: 'flex-start',
    },
  },
});

const ActionButton = withStyles(theme => ({
  root: {
    maxHeight: 24,
    padding: theme.spacing(0.25, 1, 0.25, 1),
    minWidth: 100,
    fontSize: 11,
    marginRight: theme.spacing(1),
    '&[hidden=true]': {
      display: 'none',
    },
  },
}))(RoundButton);

const Tooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: theme.palette.common.white,
  },
  arrow: {
    color: theme.palette.facet.accentBlue,
  },
}))(MuiTooltip);

function ListItemActions({
  className,
  classes,
  resource,
  fileLinkCollection,
  setFileLinkCollection,
  setProgress,
  selectedAccountId,
  setSelectedAccountId,
  toggleHide,
  hasAccounts,
  uploadProgress,
  completed,
  markAsComplete,
  showMarkCompleteTooltip,
  useGoalModal,
}) {
  const { isAgent } = useUserContext();
  const theme = useTheme();
  const hidden = resource.attr('hidden') === 'true';
  const userCanUpdateActionItem = resource.attr('isReadOnly') !== 'true';
  const fileUploadUrl = resource.relationship('uploadSecureFiles')
    ? resource
      .relationship('uploadSecureFiles')
      .link('related')
      .attr('url')
    : '';
  const disableAddFileButton = selectedAccountId === undefined || selectedAccountId < 1;

  async function handleAccountWizardClick(relationship) {
    const [accountWizardStep] = await relationship?.link('related').get();
    navigateToClientPage(accountWizardStep.link('self'));
  }

  const isAccountAi = Boolean(resource?.relationship(relationships.accountOpeningWizard.name)?.link('related'));
  const isAccountFundingAi = resource?.attr('actionItemType') === 'fund-account';
  const isAccountRetitlingAi = resource.withRelationship(relationships.accountRetitlingWizard.name, () => true, () => false);

  const trustApprovalRelationship = resource?.relationship(fr.relationships.approval.name)?.link('related');

  const isTrustApproval = Boolean(trustApprovalRelationship);

  const completeButtonStyle = completed
    ? {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white,
      marginRight: 0,
    }
    : {
      marginRight: 0,
    };

  const handleAccountFundingOnClick = () => {
    if (!completed) {
      navigateToFunding(resource);
    }
  };

  let ComponentVoidedCheckAddAccountOrPicklist = null;
  if (hasAccounts === null) {
    ComponentVoidedCheckAddAccountOrPicklist = <div />;
  } else if (hasAccounts) {
    ComponentVoidedCheckAddAccountOrPicklist = (
      <AccountPicklist
        onSelect={accountID => setSelectedAccountId(accountID)}
      />);
  }

  const addFileButtonLabel = 'Add File';

  const addFileButton = disableAddFileButton ? (
    //  Wallace 1/19/2021 - The add file button should only be disable-able for voided checks.
    <Tooltip title="Select an account in order to add a file.">
      <span>
        <ActionButton
          startIcon={<Description />}
          size="small"
          color="primary"
          variant="contained"
          hidden={completed || !userCanUpdateActionItem}
          disabled
        >
          {addFileButtonLabel}
        </ActionButton>
      </span>
    </Tooltip>
  ) : (
    <ActionButton
      startIcon={<Description />}
      size="small"
      color="primary"
      variant="contained"
      hidden={completed || !userCanUpdateActionItem}
    >
      {addFileButtonLabel}
    </ActionButton>
  );

  const ComponentFileUpload = !fileUploadUrl ? null : (
    <Uploader
      url={fileUploadUrl}
      setUploadProgress={(v) => {
        setProgress(v);
      }}
      onSuccess={() => fileLinkCollection.refresh().then((coll) => {
        setFileLinkCollection(coll);
      }).catch((err) => {
        displayErrorMessage(err,
          'Failed to refresh files, please try refreshing the page.');
      })
        }
      successToastMessage="Successfully uploaded file(s)"
    >
      {uploadProgress ? (
        <LinearProgress
          color="accentGreen"
          className={classes.progressBar}
          visible
          text={uploadProgress === 100 ? 'Processing...' : ''}
          textVariant="body1"
          ProgressProps={{
            variant: 'determinate',
            value: uploadProgress,
          }}
        />
      ) : (addFileButton)}
    </Uploader>
  );

  if (isAccountFundingAi) {
    return (
      <div className={classNames(classes.root, classes.actions, className)}>

        <div className={classes.actions}>
          <ActionButton
            test={'fund-account-button'}
            color="secondary"
            style={completeButtonStyle}
            size="small"
            variant="contained"
            onClick={handleAccountFundingOnClick}
          >
            {completed ? 'Completed' : 'Click to Fund'}
          </ActionButton>
        </div>
      </div>

    );
  }


  if (isAccountAi) {
    return (
      <div className={classNames(classes.root, classes.actions, className)}>
        <div className={classes.actions}>
          <ActionButton
            test={'complete-account-button'}
            color="secondary"
            style={completeButtonStyle}
            size="small"
            variant="contained"
            onClick={async () => {
              if (!completed && resource?.relationship(relationships.accountOpeningWizard.name)) {
                await handleAccountWizardClick(resource.relationship(relationships.accountOpeningWizard.name));
              }
            }}
          >
            {completed ? 'Completed' : 'Complete Opening'}
          </ActionButton>
        </div>
      </div>
    );
  }

  if (isAccountRetitlingAi) {
    const actionCopy = resource.withRelationship(relationships.accountRetitlingWizard.name, (rel) => rel.meta('prompt'), () => 'Complete Retitling');
    return (
      <div className={classNames(classes.root, classes.actions, className)}>
        <div className={classes.actions}>
          <ActionButton
            test={'retitle-account-button'}
            color="secondary"
            style={completeButtonStyle}
            size="small"
            variant="contained"
            onClick={async () => {
              if (!completed && resource?.relationship(relationships.accountRetitlingWizard.name)) {
                await handleAccountWizardClick(resource.relationship(relationships.accountRetitlingWizard.name));
              }
            }}
          >
            {completed ? 'Completed' : actionCopy}
          </ActionButton>
        </div>
      </div>
    );
  }

  if (isTrustApproval) {
    return (
      <div className={classNames(classes.root, classes.actions, className)}>
        <div className={classes.actions}>
          {!completed && (
          <ActionButton
            disabled={completed}
            test={'trust-approval-ai-button'}
            color="secondary"
            style={completeButtonStyle}
            size="small"
            variant="contained"
            onClick={() => {
              openModal(
                buildModalQuery(modalConsts.trustApproval, {
                  approvalLink: trustApprovalRelationship.url(),

                }),
              );
            }}
          >
            {'Upload'}
          </ActionButton>
          )}
          <ActionButton
            color="primaryGray"
            size="small"
            onClick={toggleHide}
            hidden={!completed || !userCanUpdateActionItem}
          >
            {hidden ? 'Un-hide' : 'Hide'}
          </ActionButton>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(classes.root, classes.actions, className)}>
      <div className={classes.actions}>
        { ComponentVoidedCheckAddAccountOrPicklist }
        <ActionButton
          color="primaryGray"
          size="small"
          onClick={toggleHide}
          hidden={!completed || !userCanUpdateActionItem}
        >
          {hidden ? 'Un-hide' : 'Hide'}
        </ActionButton>
        { ComponentFileUpload }
        <ActionButton
          startIcon={<EditIcon />}
          size="small"
          color="accentBlue"
          variant="contained"
          onClick={() => {
            if (isAgent) {
              addQuery({ todoId: resource.id() });
              openModal(modalConsts.simpleActionItemModal);
            } else {
              openModal(
                buildModalQuery(useGoalModal ? modalConsts.goalActionItemModal : modalConsts.clientActionItemModal, {
                  actionItemId: resource.id(),
                }),
              );
            }
          }}
          hidden={completed || !userCanUpdateActionItem}
        >
          {'Edit'}
        </ActionButton>
        <Tooltip
          arrow
          open={showMarkCompleteTooltip}
          title="Did you want to mark this as complete?"
        >
          <ActionButton
            disabled={completed}
            startIcon={completed ? <CheckCircle /> : undefined}
            color="primaryGray"
            style={completeButtonStyle}
            size="small"
            variant="contained"
            onClick={markAsComplete}
            hidden={!completed && !userCanUpdateActionItem}
          >
            {completed ? 'Completed' : 'Mark Complete'}
          </ActionButton>
        </Tooltip>
      </div>
    </div>

  );
}

ListItemActions.defaultProps = {
  className: '',
  fileLinkCollection: null,
  uploadProgress: null,
  completed: false,
  hasAccounts: null,
  selectedAccountId: null,
  useGoalModal: false,
};

ListItemActions.propTypes = {
  setProgress: PropTypes.func.isRequired,
  setFileLinkCollection: PropTypes.func.isRequired,
  fileLinkCollection: PropTypes.object,
  uploadProgress: PropTypes.number,
  useGoalModal: PropTypes.bool,
  showMarkCompleteTooltip: PropTypes.bool.isRequired,
  completed: PropTypes.bool,
  hasAccounts: PropTypes.bool,
  toggleHide: PropTypes.func.isRequired,
  selectedAccountId: PropTypes.oneOfType(
    [
      PropTypes.string,
      PropTypes.number,
    ],
  ),
  markAsComplete: PropTypes.func.isRequired,
  setSelectedAccountId: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(ListItemActions);
